import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, Edit2, Save, GripVertical, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/Button';
import { supabaseClient } from '../lib/supabase';
import { useToast } from './ui/Toast';
interface MenuItemFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: any) => void;
  editingItem?: any;
  availableCategories?: string[];
}
interface SectionOption {
  id?: string;
  name: string;
  priceModifier: number;
  available: boolean;
  displayOrder: number;
}
interface MenuSection {
  id?: string;
  name: string;
  description: string;
  required: boolean;
  minSelections: number;
  maxSelections: number;
  displayOrder: number;
  options: SectionOption[];
}
export function MenuItemFormModal({
  isOpen,
  onClose,
  onSave,
  editingItem,
  availableCategories = ['Platos Principales', 'Entradas', 'Ensaladas', 'Pastas', 'Sandwiches']
}: MenuItemFormModalProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'sections'>('info');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: availableCategories[0] || 'Platos Principales',
    price: '',
    image: '',
    catalogType: 'Almuerzo',
    available: true
  });
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<number | null>(null);
  const {
    showToast
  } = useToast();
  useEffect(() => {
    if (editingItem) {
      setFormData({
        name: editingItem.name || '',
        description: editingItem.description || '',
        category: editingItem.category || availableCategories[0] || 'Platos Principales',
        price: editingItem.price?.toString() || '',
        image: editingItem.image || '',
        catalogType: editingItem.catalogType || 'Almuerzo',
        available: editingItem.available !== false
      });
      // Load sections if editing
      if (editingItem.id) {
        loadSections(editingItem.id);
      }
    } else {
      setFormData({
        name: '',
        description: '',
        category: availableCategories[0] || 'Platos Principales',
        price: '',
        image: '',
        catalogType: 'Almuerzo',
        available: true
      });
      setSections([]);
    }
    setActiveTab('info');
  }, [editingItem, isOpen, availableCategories]);
  const loadSections = async (menuItemId: string) => {
    setLoadingSections(true);
    try {
      const data = await supabaseClient.getMenuItemSections(menuItemId);
      setSections(data);
    } catch (error) {
      console.error('Error loading sections:', error);
    } finally {
      setLoadingSections(false);
    }
  };
  const handleAddSection = () => {
    const newSection: MenuSection = {
      name: '',
      description: '',
      required: false,
      minSelections: 0,
      maxSelections: 1,
      displayOrder: sections.length,
      options: []
    };
    setSections([...sections, newSection]);
    setEditingSectionIndex(sections.length);
    setExpandedSectionIndex(sections.length);
  };
  const handleSaveSection = async (index: number) => {
    if (!editingItem?.id) {
      showToast('warning', 'Guardar plato', 'Primero guarda el plato antes de agregar secciones');
      return;
    }
    const section = sections[index];
    if (!section.name.trim()) {
      showToast('warning', 'Nombre requerido', 'La sección debe tener un nombre');
      return;
    }
    try {
      if (section.id) {
        await supabaseClient.updateMenuSection(section.id, {
          name: section.name,
          description: section.description,
          required: section.required,
          minSelections: section.minSelections,
          maxSelections: section.maxSelections,
          displayOrder: section.displayOrder
        });
      } else {
        const newSection = await supabaseClient.createMenuSection({
          ...section,
          menuItemId: editingItem.id
        });
        const updatedSections = [...sections];
        updatedSections[index] = {
          ...section,
          id: newSection.id
        };
        setSections(updatedSections);
      }
      setEditingSectionIndex(null);
      showToast('success', 'Guardado', 'Sección guardada correctamente');
    } catch (error) {
      console.error('Error saving section:', error);
      showToast('error', 'Error', 'No se pudo guardar la sección');
    }
  };
  const handleDeleteSection = async (index: number) => {
    const section = sections[index];
    if (section.id) {
      if (!window.confirm('¿Eliminar esta sección y todas sus opciones?')) return;
      try {
        await supabaseClient.deleteMenuSection(section.id);
        showToast('success', 'Eliminado', 'Sección eliminada');
      } catch (error) {
        console.error('Error deleting section:', error);
        showToast('error', 'Error', 'No se pudo eliminar');
        return;
      }
    }
    const newSections = sections.filter((_, i) => i !== index);
    setSections(newSections);
    if (editingSectionIndex === index) setEditingSectionIndex(null);
    if (expandedSectionIndex === index) setExpandedSectionIndex(null);
  };
  const handleAddOption = (sectionIndex: number) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].options.push({
      name: '',
      priceModifier: 0,
      available: true,
      displayOrder: updatedSections[sectionIndex].options.length
    });
    setSections(updatedSections);
  };
  const handleSaveOption = async (sectionIndex: number, optionIndex: number) => {
    const section = sections[sectionIndex];
    const option = section.options[optionIndex];
    if (!section.id) {
      showToast('warning', 'Guardar sección', 'Primero guarda la sección');
      return;
    }
    if (!option.name.trim()) {
      showToast('warning', 'Nombre requerido', 'La opción debe tener un nombre');
      return;
    }
    try {
      if (option.id) {
        await supabaseClient.updateSectionOption(option.id, {
          name: option.name,
          priceModifier: option.priceModifier,
          available: option.available,
          displayOrder: option.displayOrder
        });
      } else {
        const newOption = await supabaseClient.createSectionOption({
          ...option,
          sectionId: section.id
        });
        const updatedSections = [...sections];
        updatedSections[sectionIndex].options[optionIndex] = {
          ...option,
          id: newOption.id
        };
        setSections(updatedSections);
      }
      showToast('success', 'Guardado', 'Opción guardada');
    } catch (error) {
      console.error('Error saving option:', error);
      showToast('error', 'Error', 'No se pudo guardar');
    }
  };
  const handleDeleteOption = async (sectionIndex: number, optionIndex: number) => {
    const section = sections[sectionIndex];
    const option = section.options[optionIndex];
    if (option.id) {
      try {
        await supabaseClient.deleteSectionOption(option.id);
      } catch (error) {
        console.error('Error deleting option:', error);
        showToast('error', 'Error', 'No se pudo eliminar');
        return;
      }
    }
    const updatedSections = [...sections];
    updatedSections[sectionIndex].options = updatedSections[sectionIndex].options.filter((_, i) => i !== optionIndex);
    setSections(updatedSections);
  };
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" data-id="element-291">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl border border-gray-200 dark:border-gray-800 my-8 max-h-[90vh] flex flex-col" data-id="element-292">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-293">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-294">
            {editingItem ? 'Editar Plato' : 'Nuevo Plato'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-295">
            <X size={20} data-id="element-296" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-800 px-6" data-id="element-297">
          <button onClick={() => setActiveTab('info')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'info' ? 'border-black dark:border-white text-black dark:text-white' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-298">
            Información Básica
          </button>
          <button onClick={() => setActiveTab('sections')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'sections' ? 'border-black dark:border-white text-black dark:text-white' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-299">
            Opciones y Secciones
            {sections.length > 0 && <span className="ml-2 px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded-full text-xs" data-id="element-300">
                {sections.length}
              </span>}
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-301">
          {activeTab === 'info' ? <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-302">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-303">
                <div data-id="element-304">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-305">
                    Nombre del Plato
                  </label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Ej: Hamburguesa Clásica" data-id="element-306" />
                </div>

                <div data-id="element-307">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-308">
                    Precio Base (RD$)
                  </label>
                  <input type="number" required step="0.01" value={formData.price} onChange={e => setFormData({
                ...formData,
                price: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="450.00" data-id="element-309" />
                </div>
              </div>

              <div data-id="element-310">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-311">
                  Descripción
                </label>
                <textarea required rows={3} value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Describe el plato..." data-id="element-312" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-313">
                <div data-id="element-314">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-315">
                    Categoría
                  </label>
                  <select value={formData.category} onChange={e => setFormData({
                ...formData,
                category: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-316">
                    {availableCategories.map(category => <option key={category} value={category} data-id="element-317">
                        {category}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-318">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-319">
                    Tipo de Catálogo
                  </label>
                  <select value={formData.catalogType} onChange={e => setFormData({
                ...formData,
                catalogType: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-320">
                    <option value="Almuerzo" data-id="element-321">Almuerzo</option>
                    <option value="Cena" data-id="element-322">Cena</option>
                  </select>
                </div>

                <div data-id="element-323">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-324">
                    URL de Imagen
                  </label>
                  <input type="url" value={formData.image} onChange={e => setFormData({
                ...formData,
                image: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="https://..." data-id="element-325" />
                </div>
              </div>

              <div className="flex gap-3 pt-4" data-id="element-326">
                <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-327">
                  Cancelar
                </Button>
                <Button type="submit" fullWidth data-id="element-328">
                  {editingItem ? 'Actualizar Plato' : 'Guardar Plato'}
                </Button>
              </div>
            </form> :
        // Sections Tab
        <div className="p-6" data-id="element-329">
              {!editingItem?.id ? <div className="text-center py-10 bg-gray-50 dark:bg-gray-800 rounded-xl" data-id="element-330">
                  <p className="text-gray-500 dark:text-gray-400 mb-4" data-id="element-331">
                    Primero guarda el plato para poder agregar opciones y
                    secciones
                  </p>
                  <Button onClick={() => setActiveTab('info')} data-id="element-332">
                    Ir a Información Básica
                  </Button>
                </div> : loadingSections ? <div className="flex justify-center py-10" data-id="element-333">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" data-id="element-334"></div>
                </div> : <div className="space-y-4" data-id="element-335">
                  {sections.map((section, index) => <div key={section.id || index} className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-336">
                      <div className="p-4 flex items-center gap-4" data-id="element-337">
                        <button onClick={() => setExpandedSectionIndex(expandedSectionIndex === index ? null : index)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-338">
                          {expandedSectionIndex === index ? <ChevronUp size={20} data-id="element-339" /> : <ChevronDown size={20} data-id="element-340" />}
                        </button>

                        <div className="flex-1" data-id="element-341">
                          {editingSectionIndex === index ? <div className="space-y-3" data-id="element-342">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-343">
                                <input type="text" value={section.name} onChange={e => {
                        const updated = [...sections];
                        updated[index].name = e.target.value;
                        setSections(updated);
                      }} placeholder="Nombre (ej: Elige proteína)" className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" data-id="element-344" />
                                <input type="text" value={section.description} onChange={e => {
                        const updated = [...sections];
                        updated[index].description = e.target.value;
                        setSections(updated);
                      }} placeholder="Descripción (opcional)" className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" data-id="element-345" />
                              </div>
                              <div className="flex items-center gap-4 text-sm flex-wrap" data-id="element-346">
                                <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300" data-id="element-347">
                                  <input type="checkbox" checked={section.required} onChange={e => {
                          const updated = [...sections];
                          updated[index].required = e.target.checked;
                          setSections(updated);
                        }} className="rounded border-gray-300 text-black focus:ring-black" data-id="element-348" />
                                  Obligatorio
                                </label>
                                <div className="flex items-center gap-2" data-id="element-349">
                                  <span className="text-gray-500" data-id="element-350">Min:</span>
                                  <input type="number" min="0" value={section.minSelections} onChange={e => {
                          const updated = [...sections];
                          updated[index].minSelections = parseInt(e.target.value);
                          setSections(updated);
                        }} className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" data-id="element-351" />
                                </div>
                                <div className="flex items-center gap-2" data-id="element-352">
                                  <span className="text-gray-500" data-id="element-353">Max:</span>
                                  <input type="number" min="1" value={section.maxSelections} onChange={e => {
                          const updated = [...sections];
                          updated[index].maxSelections = parseInt(e.target.value);
                          setSections(updated);
                        }} className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" data-id="element-354" />
                                </div>
                              </div>
                            </div> : <div data-id="element-355">
                              <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-356">
                                {section.name || 'Nueva Sección'}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-357">
                                {section.required ? 'Obligatorio' : 'Opcional'}{' '}
                                • {section.minSelections}-
                                {section.maxSelections} selecciones
                              </p>
                            </div>}
                        </div>

                        <div className="flex items-center gap-2" data-id="element-358">
                          {editingSectionIndex === index ? <Button size="sm" onClick={() => handleSaveSection(index)} leftIcon={<Save size={16} data-id="element-360" />} data-id="element-359">
                              Guardar
                            </Button> : <Button size="sm" variant="outline" onClick={() => setEditingSectionIndex(index)} leftIcon={<Edit2 size={16} data-id="element-362" />} data-id="element-361">
                              Editar
                            </Button>}
                          <button onClick={() => handleDeleteSection(index)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-363">
                            <Trash2 size={18} data-id="element-364" />
                          </button>
                        </div>
                      </div>

                      {expandedSectionIndex === index && <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900" data-id="element-365">
                          <div className="space-y-2 mb-4" data-id="element-366">
                            {section.options.map((option, optIndex) => <div key={option.id || optIndex} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-367">
                                <GripVertical size={16} className="text-gray-400 cursor-move" data-id="element-368" />
                                <input type="text" value={option.name} onChange={e => {
                      const updated = [...sections];
                      updated[index].options[optIndex].name = e.target.value;
                      setSections(updated);
                    }} placeholder="Nombre opción" className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" data-id="element-369" />
                                <div className="flex items-center gap-2" data-id="element-370">
                                  <span className="text-sm text-gray-500" data-id="element-371">
                                    +RD$
                                  </span>
                                  <input type="number" step="0.01" value={option.priceModifier} onChange={e => {
                        const updated = [...sections];
                        updated[index].options[optIndex].priceModifier = parseFloat(e.target.value) || 0;
                        setSections(updated);
                      }} placeholder="0" className="w-20 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" title="Precio adicional (opcional)" data-id="element-372" />
                                </div>
                                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-373">
                                  <input type="checkbox" checked={option.available} onChange={e => {
                        const updated = [...sections];
                        updated[index].options[optIndex].available = e.target.checked;
                        setSections(updated);
                      }} className="rounded border-gray-300 text-black focus:ring-black" data-id="element-374" />
                                  Disp.
                                </label>
                                <button onClick={() => handleSaveOption(index, optIndex)} className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors" title="Guardar opción" data-id="element-375">
                                  <Check size={16} data-id="element-376" />
                                </button>
                                <button onClick={() => handleDeleteOption(index, optIndex)} className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors" title="Eliminar opción" data-id="element-377">
                                  <Trash2 size={16} data-id="element-378" />
                                </button>
                              </div>)}
                          </div>
                          <Button size="sm" variant="outline" onClick={() => handleAddOption(index)} leftIcon={<Plus size={16} data-id="element-380" />} fullWidth data-id="element-379">
                            Agregar Opción
                          </Button>
                        </div>}
                    </div>)}

                  {sections.length === 0 && <div className="text-center py-10 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-xl" data-id="element-381">
                      No hay secciones configuradas. Agrega tu primera sección.
                    </div>}

                  <Button onClick={handleAddSection} leftIcon={<Plus size={18} data-id="element-383" />} fullWidth data-id="element-382">
                    Nueva Sección
                  </Button>
                </div>}
            </div>}
        </div>
      </div>
    </div>;
}